<template>
  <div id="create-article-textarea">
    <create-article-title-field
      @toggleList="toggleList = $event"
      @updateTitleAndSlug="updateTitleAndSlug($event)"
    />
    <transition-group
      name="animate-route"
      mode="out-in"
      enter-active-class="animated fadeIn"
      leave-active-class="animated fadeOut"
    >
      <v-card
        v-if="!toggleList"
        key="article-text"
        id="article-text"
        :class="{ 'error-outline': contentError, 'disable-styling': !markdownStyling }"
      >
        <editor
          ref="toastuiEditor"
          :options="defaultOptions"
          height="70vh"
          initialEditType="markdown"
          previewStyle="tab"
          @change="onEditorChange"
          @load="onEditorLoad"
        />
      </v-card>

      <div v-else key="article-list" id="article-list">
        <admin-articles />
      </div>
    </transition-group>
  </div>
</template>

<script>
import { Vue, Component, Watch } from "vue-property-decorator";
import CreateArticleTitleField from "./CreateArticleTitleField";
import ArticleList from "@/components/ArticleList/ArticleList";
import AdminArticles from "@/views/AdminArticles";
import { eventBus } from "@/main";
import "codemirror/lib/codemirror.css";
import "@toast-ui/editor/dist/toastui-editor.css";
import { Editor } from "@toast-ui/vue-editor";

@Component({
  components: {
    ArticleList,
    AdminArticles,
    CreateArticleTitleField,
    editor: Editor,
  },
})
export default class CreateArticleTextarea extends Vue {
  isTyping = false

  defaultOptions = {
    placeholder: this.$store.state.user.langEng ? 'Article text' : 'Artikel text',
    language: "en-US",
    useCommandShortcut: true,
    useDefaultHTMLSanitizer: false,
    usageStatistics: false,
    hideModeSwitch: true,
    hooks: {
    addImageBlobHook: async blob => {
      let res = await this.uploadImagefromEditor(blob)
      let image = `\n![](${res.path})\n`
      this.$refs.toastuiEditor.editor.insertText(image)
      return false;
    }
  },
    toolbarItems: [
      "heading",
      "bold",
      "italic",
      "strike",
      "divider",
      "hr",
      "quote",
      "divider",
      "ul",
      "ol",
      "task",
      "indent",
      "outdent",
      "divider",
      "table",
      "image",
      "link",
      "divider",
      "code",
      "codeblock",
      {
        type: 'button',
        options: {
          class: 'last',
          tooltip: 'Toggle markdown styling',
          event: 'toggleMarkdownStyling',
          text: 'S',
          style: `
            color: inherit;
            background:none;
            font-weight: bold;  
            font-size: 1em;
            line-height: 1em;
          `
        }
      },
      {
        type: 'button',
        options: {
          class: 'last',
          tooltip: this.$store.state.user.langEng ? 'Increase font size' : 'Öka teckenstorlek',
          event: 'increaseFontSize',
          text: '+',
          style: `
            color: inherit;
            background:none;
            font-weight: bold;  
            font-size: 1.4em;
            line-height: 0.7em;
          `
        }
      },
      {
        type: 'button',
        options: {
          class: 'last',
          tooltip: this.$store.state.user.langEng ? 'Decrease font size' : 'Minska teckenstorlek',
          event: 'decreaseFontSize',
          text: '-',
          style: `
            color: inherit;
            background:none;
            font-weight: bold;  
            font-size: 1.5em;
            line-height: 0;
          `
        }
      },
    ],
  };

  @Watch('$store.state.user.editorFontSize')
  onEditorFontSizeChange() {
    const editor = document.querySelector('.CodeMirror-lines')
    editor && editor.style.setProperty('font-size', this.$store.state.user.editorFontSize + 'em')
  }

  @Watch('$store.state.article.currentArticle.content', {immediate: true})
  onArticleChange(content) {
    if(this.isTyping) return

    setTimeout(() => {
      this.$refs.toastuiEditor.invoke(`setMarkdown`, content, false)
    }, 0);
  }

  toggleMarkdownStyling() {
    this.$store.commit('toggleEditorStyling')
  }

  increaseFontSize() {
    this.$store.commit('increaseEditorFontSize')
  }

  decreaseFontSize() {
    this.$store.commit('decreaseEditorFontSize')
  }

  onEditorLoad() {
    this.onEditorFontSizeChange()
  }

  onEditorChange() {
    if(this.initEditor) {
      this.isTyping = true
      let md = this.$refs.toastuiEditor.invoke("getMarkdown")
      this.article.content = md
      
      clearTimeout(this.throttleInpuit)
      this.throttleInpuit = setTimeout(() => {
        this.isTyping = false
      }, 50);

    }
    this.initEditor = true
  }

  async uploadImagefromEditor(image) {
    let formData = new FormData()
    formData.append('file', image, image.name)

    let res = await fetch('/api/file-upload', {
      method: 'POST',
      body: formData
    })
    return await res.json()
  }

  //Computed methods
  get article() {
    return this.$store.state.article.currentArticle;
  }
  get markdownStyling() {
    return this.$store.state.user.showEditorStyling
  } 
  get toggleList() {
    return this.$store.state.article.showCreateArticleList;
  }
  set toggleList(val) {
    this.$store.commit("toggleCreateArticleList", val);
  }
  get contentError() {
    return (
      this.$store.state.article.contentError && !this.article.content.length
    );
  }
  get articles() {
    return this.$store.state.article.articles;
  }

  addListingsFromZip(data) {
    // to fix textarea rows bug
    // the component needs to be rendered before
    // new code is auto listed

    const filename = data.dlButton.split("/").pop() || "";
    this.article.content += `\n\n\n<a href="${
      data.dlButton
    }"><button class="btn-small">Ladda hem koden som ${filename}</button></a>\n`;

    for (let order of data.order) {
      let file = data.files.filter((f) => f.id === order)[0];
      this.article.content +=
        "\n#### " +
        file.path +
        "\n```" +
        file.extension +
        "\n" +
        file.content +
        "\n```\n";
    }
  }

  addImageToMarkdown(image) {
    this.insertText(`\n![](${image})\n`)
  }

  addFileToContentArea(data) {
    this.insertText(`\n<a target="_blank" href="${data.id}">${data.name}</a>\n`)
  }

  insertText(text) {
    setTimeout(() => {
      this.$refs.toastuiEditor.editor.insertText(text)
    }, 0);
  }

  mounted() {
    let editor = this.$refs.toastuiEditor.editor
    editor.eventManager.addEventType('toggleMarkdownStyling');
    editor.eventManager.listen('toggleMarkdownStyling', this.toggleMarkdownStyling)
    editor.eventManager.addEventType('increaseFontSize');
    editor.eventManager.listen('increaseFontSize', this.increaseFontSize)
    editor.eventManager.addEventType('decreaseFontSize');
    editor.eventManager.listen('decreaseFontSize', this.decreaseFontSize)

    eventBus.$on("selectedListing", (data) => this.addListingsFromZip(data));
    eventBus.$on("addImageToMarkdown", (image) =>
      this.addImageToMarkdown(image)
    );
    eventBus.$on("addFileToContentArea", (data) =>
      this.addFileToContentArea(data)
    );
  }

  beforeDestroy() {
    eventBus.$off("selectedListing");
    eventBus.$off("addImageToMarkdown");
    eventBus.$off("addFileToContentArea");
  }
}
</script>

<style lang="scss">
#create-article #article-text {
  height: auto !important;
}
#create-article-textarea {
  animation-duration: 150ms;
  #article-list {
    padding-left: 1em;
  }

  .CodeMirror-scroll, .tui-editor-defaultUI-toolbar {
    background-color: var(--main-background-color);
  }

  .CodeMirror-line {
    padding-left: 15px;
    padding-right: 15px;
  }

  .te-markdown-tab-section {
    display: none !important;
  }

  .disable-styling {
    .tui-md-heading {
      font-size: 1.1em;
      line-height: 1.1em;
    }
    .tui-md-marked-text, 
    .tui-md-delimiter, 
    .tui-md-thematic-break, 
    .tui-md-link, 
    .tui-md-table, 
    .tui-md-block-quote,
    .tui-md-code-block.tui-md-meta, 
    .tui-md-code,
    .tui-md-link-desc, 
    .tui-md-list-item-odd, 
    .tui-md-list-item-bullet,
    .tui-md-html {
      color: var(--main-text-color);
      line-height: 1em;
    }
    .tui-md-code, .tui-md-code-block.CodeMirror-linebackground {
      background-color: transparent;
    }
  }
}
.text-content textarea {
  resize: none;
  height: 75vh;
}
#text-content-focus {
  margin: 0;
  padding: 0;
  padding-top: 5px;
}

.mobile {
  .CodeMirror-line {
    padding-left: 5px !important;
    padding-right: 5px !important;
  }
}
</style>
